import React, { useRef, useState } from 'react';
import { observer } from 'mobx-react-lite';
import { useStore } from '../../../contexts/StoreContext';
import GestureArea from '../../gesture-area/GestureArea';
import {
  GestureStateChangeEvent,
  GestureUpdateEvent,
  LongPressGestureHandlerEventPayload,
  PanGestureHandlerEventPayload,
  PinchGestureHandlerEventPayload,
} from 'react-native-gesture-handler';
import useResponsiveScreen from '../../../hooks/useResponsiveScreen';
import { UserImageWithTransform as UserImage } from '../../user-image/UserImageWithTransform';
import { IUserImageAreaModel } from '../../../store/_models/userImageArea';
import { IUserImageModel } from '../../../store/_models/userImage';
import { calculateZoomValue } from '../../../helpers/zoom.helper';
import { UserImageAreaCursor } from '../../../screen/picture_view/components/UserImageAreaCursor';
import { UserImageArea } from '@photosi/rn-components/user-image-area/UserImageArea';
import { UserImageAreaGridOverlay } from '@photosi/rn-components/user-image-area/user-image-area-grid-overlay/UserImageAreaGridOverlay';
import { UserImageAreaSelectionOverlay } from '@photosi/rn-components/user-image-area/user-image-area-selection-overlay/UserImageAreaSelectionOverlay';
import { UserImageAreaEmpty } from '@photosi/rn-components/user-image-area/UserImageAreaEmpty';
import { useWheelZoom } from '../../../hooks/useWheelZoom';
import { useResponsiveScale } from '../../../contexts/ResponsiveScaleContext';
import { BadgeQualityAlert } from '@photosi/rn-components/badge-quality-alert/BadgeQualityAlert';
import { UserImageAreaColorOverlay } from '@photosi/rn-components/user-image-area/user-image-area-color-overlay/UserImageAreaColorOverlay';
import { UserImageAreaOverlayStyle } from '@photosi/rn-components/user-image-area/UserImageArea.constants';
import { useDragCursorHandlers } from './useDragCursorHandlers';
import { PreviewSvgModelInstance } from 'src/store/_models/previewSvg';
import { emit } from '../../../contexts/TransformEventBus';
import { BadgeCropAlert } from '@photosi/rn-components/badge-crop-alert/BadgeCropAlert';

export type MultiPreviewContentProps = {
  preview: PreviewSvgModelInstance;
  editable?: boolean;
  showGrid?: boolean;
  disableSwapImageAnimation?: boolean;
  swappable?: boolean;
  shownFullScreen?: boolean;
  disabled?: boolean;
  emptyStateWithText?: boolean;
};

/**
 * Represents the collage of all the images to be inserted into the
 * photo space (userImageAreas) in the product (userImagesForPreviewSvg).
 */
export const MultiPreviewContent = observer(
  ({
    preview,
    editable = false,
    showGrid = false,
    swappable = false,
    disabled = false,
    disableSwapImageAnimation = false,
    emptyStateWithText = false,
  }: MultiPreviewContentProps) => {
    const {
      project: { selectUserImage, activeUserImageArea },
    } = useStore();
    const { responsiveScale } = useResponsiveScale();
    const { isMobile } = useResponsiveScreen();
    const lastPinchZoomRef = useRef<number | undefined>();
    const [hoveringAreaId, setHoveringAreaId] = useState<string | null>(null);

    const {
      dragCursor,
      dragCursorStart,
      dragCursorChange,
      dragCursorEnd,
      dragCursorFinalize,
      isCursorStartingArea,
      isCursorEndingArea,
      isCursorEnabled,
      isCursorDragging,
    } = useDragCursorHandlers(preview);

    // Zoom with wheel logic
    const isHoveringOnActiveUserImageArea = !!hoveringAreaId && hoveringAreaId === activeUserImageArea?.id;
    const isZoomWithWheelActive = isHoveringOnActiveUserImageArea && !preview.isAnyUserImageUpdating;
    useWheelZoom({ isActive: isZoomWithWheelActive, userImage: activeUserImageArea?.userImage });

    const isMultiPhoto = preview.isMultiphoto;

    const userImageAreas = preview.userImageAreas;

    const isSwapImageEnabled = swappable && isMultiPhoto;

    /**
     * GESTURE HANDLERS
     */
    const onLongPressHandler = (
      userImageArea: IUserImageAreaModel,
      event: GestureStateChangeEvent<LongPressGestureHandlerEventPayload>,
    ) => {
      if (activeUserImageArea?.userImage) {
        // se ho già selezionato un'immagine
        // do nothing
      } else if (isSwapImageEnabled && isMobile) {
        // se ho abilitato lo swap
        dragCursorStart({
          x: userImageArea.x * responsiveScale + event.x,
          y: userImageArea.y * responsiveScale + event.y,
        });
      }
    };

    const panStartHandler = (
      userImageArea: IUserImageAreaModel,
      event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
    ) => {
      if (activeUserImageArea?.userImage) {
        // se ho già selezionato un'immagine
        // do nothing
      } else if (isSwapImageEnabled && !isMobile) {
        // se ho abilitato lo swap
        dragCursorStart({
          x: userImageArea.x * responsiveScale + event.x,
          y: userImageArea.y * responsiveScale + event.y,
        });
      }
    };

    const panHandler = (
      userImageArea: IUserImageAreaModel,
      event: GestureUpdateEvent<PanGestureHandlerEventPayload>,
    ) => {
      if (activeUserImageArea?.userImage) {
        // se ho già selezionato un'immagine
        dragImageChange(event, activeUserImageArea?.userImage);
      } else if (isSwapImageEnabled) {
        // se ho abilitato lo swap
        dragCursorChange({
          x: userImageArea.x * responsiveScale + event.x,
          y: userImageArea.y * responsiveScale + event.y,
        });
      }
    };

    const panEndHandler = (
      userImageArea: IUserImageAreaModel,
      event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
    ) => {
      if (activeUserImageArea?.userImage) {
        // se ho già selezionato un'immagine
        dragImageEnd(event, activeUserImageArea?.userImage);
      } else if (isSwapImageEnabled) {
        // se ho abilitato lo swap
        dragCursorEnd({
          x: userImageArea.x * responsiveScale + event.x,
          y: userImageArea.y * responsiveScale + event.y,
        });
      }
    };

    const panFinalizeHandler = () => {
      // resetto il cursore e lo scroll
      dragCursorFinalize();
    };

    // Calculate the new image scale using a dynamic speed approach
    const calculateScaleFrom = (userImage: IUserImageModel, eventScale: number) => {
      if (lastPinchZoomRef.current === undefined) {
        lastPinchZoomRef.current = eventScale;
      }
      const zoomFactor = eventScale / lastPinchZoomRef.current;
      lastPinchZoomRef.current = eventScale;
      return calculateZoomValue(userImage.scale * zoomFactor, userImage.minScale, userImage.maxScale);
    };

    const pinchHandler = (event: GestureUpdateEvent<PinchGestureHandlerEventPayload>) => {
      if (activeUserImageArea?.userImage) {
        const newScale = calculateScaleFrom(activeUserImageArea.userImage, event.scale);

        // activeUserImageArea?.userImage.setScale(newScale, responsiveScale);
        emit({
          type: 'temp-zoom',
          userImageId: activeUserImageArea?.userImage.id,
          payload: { absoluteScale: newScale },
          source: 'gesture',
        });
      }
    };

    const pinchEndHandler = (event: GestureStateChangeEvent<PinchGestureHandlerEventPayload>) => {
      if (activeUserImageArea?.userImage) {
        const newScale = calculateScaleFrom(activeUserImageArea.userImage, event.scale);

        // activeUserImageArea?.userImage.applyScale(newScale, responsiveScale);
        emit({
          type: 'apply-zoom',
          userImageId: activeUserImageArea?.userImage.id,
          payload: { absoluteScale: newScale },
          source: 'gesture',
        });
      }
      lastPinchZoomRef.current = undefined;
    };

    /**
     * SELECT IMAGE HANDLERS
     */

    const tapHandler = ({ userImage }: IUserImageAreaModel) => {
      return () => {
        if (!isCursorDragging) {
          selectUserImage(userImage);
        }
      };
    };

    /**
     * DRAG IMAGE HANDLERS
     */

    const dragImageChange = (event: GestureUpdateEvent<PanGestureHandlerEventPayload>, userImage: IUserImageModel) => {
      // userImage.setOffsets({ x: event.translationX, y: event.translationY });

      // NUOVO: Emit evento temporaneo direttamente da gesture (UI thread)
      emit({
        type: 'temp-pan',
        userImageId: userImage.id,
        payload: { x: event.translationX, y: event.translationY },
        source: 'gesture',
      });
    };

    const dragImageEnd = (
      event: GestureStateChangeEvent<PanGestureHandlerEventPayload>,
      userImage: IUserImageModel,
    ) => {
      // userImage.applyOffsets({ x: event.translationX, y: event.translationY }, responsiveScale);

      // NUOVO: Emit evento apply direttamente da gesture (UI thread)
      emit({
        type: 'apply-pan',
        userImageId: userImage.id,
        payload: { x: event.translationX, y: event.translationY },
        source: 'gesture',
      });
    };

    return (
      <>
        {/* DRAG CURSOR */}
        {dragCursor && (
          <UserImageAreaCursor
            x={dragCursor.x ?? 0}
            y={dragCursor.y ?? 0}
            userImageArea={userImageAreas.find(userImageArea => userImageArea.id === dragCursor.userImageAreaId)!}
          />
        )}

        {userImageAreas.map((userImageArea, indexUserImageArea) => {
          const userImage = userImageArea.userImage;
          if (!userImage) {
            return (
              <UserImageAreaEmpty
                key={userImageArea.id}
                userImageArea={userImageArea}
                disabled={disabled}
                showText={emptyStateWithText}
                focusOrder={indexUserImageArea}
              />
            );
          }

          const isSelected: boolean = activeUserImageArea?.id === userImageArea.id;
          const showGridOverlay: boolean = isSelected && editable && showGrid;
          const showSelectionOverlay: boolean = isMultiPhoto && !isMobile && !isSelected && !dragCursor;
          const panActivateAfterLongPressMs = isMobile || !editable ? 300 : 0;
          const isQualityBadgeVisible: boolean =
            userImage.qualityAlert && !!userImage.qualitySuggestions?.options.length;
          const isCropBadgeVisible: boolean = userImage.cropAlert && !editable;

          let overlayStyle;
          if (isCursorStartingArea(userImage)) overlayStyle = UserImageAreaOverlayStyle.GREY;
          if (isCursorEndingArea(userImage)) overlayStyle = UserImageAreaOverlayStyle.DARK_BLUE;
          if (isCursorEnabled(userImage)) overlayStyle = UserImageAreaOverlayStyle.LIGHT_BLUE;
          return (
            <UserImageArea userImageArea={userImageArea} key={userImageArea.id} isSelected={isSelected}>
              <GestureArea
                disabled={disabled || preview.isAnyUserImageUpdating}
                onPanStart={event => panStartHandler(userImageArea, event)}
                onPan={event => panHandler(userImageArea, event)}
                onPanEnd={event => panEndHandler(userImageArea, event)}
                onPanFinalize={() => panFinalizeHandler()}
                onPinch={event => pinchHandler(event)}
                onPinchEnd={event => pinchEndHandler(event)}
                onTap={tapHandler(userImageArea)}
                onLongPress={event => onLongPressHandler(userImageArea, event)}
                onHoverStart={() => setHoveringAreaId(userImageArea.id)}
                onHoverEnd={() => setHoveringAreaId(prev => (prev === userImageArea.id ? null : prev))}
                panActivateAfterLongPressMs={panActivateAfterLongPressMs}
              >
                {showGridOverlay && <UserImageAreaGridOverlay />}
                {overlayStyle && <UserImageAreaColorOverlay overlayStyle={overlayStyle} />}
                {showSelectionOverlay && (
                  <UserImageAreaSelectionOverlay
                    userImageArea={userImageArea}
                    isDragging={isCursorDragging}
                    index={indexUserImageArea}
                    onPress={async () => (isCursorDragging ? tapHandler(userImageArea)() : undefined)}
                  />
                )}
                <UserImage
                  editable={editable}
                  userImage={userImage}
                  disableSwapImageAnimation={disableSwapImageAnimation}
                />
              </GestureArea>
              {isQualityBadgeVisible && <BadgeQualityAlert userImage={userImage} />}
              {isCropBadgeVisible && <BadgeCropAlert userImage={userImage} />}
            </UserImageArea>
          );
        })}
      </>
    );
  },
);
